/* oxlint-disable max-lines-per-function, complexity */
/* oxlint-disable jsx-a11y/anchor-is-valid, jsx-a11y/no-noninteractive-element-to-interactive-role */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { faFileImport } from '@fortawesome/pro-light-svg-icons';
import { useOfflineModeManager, useTargetBrandConfig, useCurrentUser, useNavigationHelper } from 'FrontRoyalAngular';
import { useAngularContext } from 'AngularContext';
import { NavigationSectionKey, type NavigationSectionConfig } from 'NavigationHelpers';
import { type ProgramSwitcherProgramConfig } from 'ProgramSwitcher';
import {
    type CurrentUser,
    getCanCurrentlySubmitProgramApplication,
    getCanSubmitProgramApplications,
    userActionsApi,
} from 'Users';
import { AppMenuUserAvatarPresentation } from '../AppMenuUserAvatar';
import { ProgramSwitcherPresentation } from '../ProgramSwitcher';
import { useNavigationSections } from '../../hooks/useNavigationSections';
import { useProgramSwitcherConfigs } from '../../hooks/useProgramSwitcherConfigs';
import { type AppMenuUserPresentationProps } from './AppMenuUser.types';

// Helper for popover link styles
const popoverLinkClasses = clsx(
    'col-span-2',
    'm-0',
    'py-3',
    'px-4',
    'text-gray-700',
    'text-[15px]',
    'leading-3',
    'cursor-pointer',
    'hover:bg-slate-grey-100',
    'transition-colors',
    'block',
);

// Logged-in popover content component
function LoggedInPopoverContent({
    userName,
    showProgramHeader,
    programConfigs,
    isSwitchingProgram,
    showApplyToProgramWithoutSelectProgram,
    showApplyToProgramWithSelectProgram,
    availableSections,
    onNavSectionClick,
    onSwitchProgram,
}: {
    userName: string;
    showProgramHeader: boolean;
    programConfigs: ProgramSwitcherProgramConfig[];
    isSwitchingProgram: boolean;
    showApplyToProgramWithoutSelectProgram: boolean;
    showApplyToProgramWithSelectProgram: boolean;
    availableSections: NavigationSectionConfig[];
    onNavSectionClick: (sectionName: string, arg?: boolean) => void;
    onSwitchProgram: (config: ProgramSwitcherProgramConfig) => void;
}) {
    const { t } = useTranslation();
    const navRef = useRef<HTMLElement>(null);
    const [showTopFade, setShowTopFade] = useState(false);
    const [showBottomFade, setShowBottomFade] = useState(false);

    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return undefined;

        const updateFades = () => {
            const { scrollTop, scrollHeight, clientHeight } = nav;
            setShowTopFade(scrollTop > 0);
            setShowBottomFade(scrollTop + clientHeight < scrollHeight - 1);
        };

        updateFades();
        nav.addEventListener('scroll', updateFades);

        // Re-check when content changes (e.g., Show More toggle)
        const observer = new MutationObserver(updateFades);
        observer.observe(nav, { childList: true, subtree: true });

        return () => {
            nav.removeEventListener('scroll', updateFades);
            observer.disconnect();
        };
    }, []);

    const fadeSize = '32px';
    const maskStyle = useMemo(() => {
        if (showTopFade && showBottomFade) {
            return {
                maskImage: `linear-gradient(to bottom, transparent, black ${fadeSize}, black calc(100% - ${fadeSize}), transparent)`,
            };
        }
        if (showTopFade) {
            return { maskImage: `linear-gradient(to bottom, transparent, black ${fadeSize})` };
        }
        if (showBottomFade) {
            return { maskImage: `linear-gradient(to bottom, black calc(100% - ${fadeSize}), transparent)` };
        }
        return undefined;
    }, [showTopFade, showBottomFade]);

    return (
        <nav
            ref={navRef}
            style={maskStyle}
            className={clsx('font-medium', 'grid', 'grid-cols-2', 'max-h-[calc(100vh-80px)]', 'overflow-y-auto')}
        >
            {showProgramHeader && (
                <h3
                    className={clsx(
                        'col-span-2',
                        'm-0',
                        'px-4',
                        'pt-4',
                        'pb-2.5',
                        'text-gray-700',
                        'text-base',
                        'cursor-default',
                        'border-0',
                        'border-b',
                        'border-solid',
                        'border-slate-grey-200',
                    )}
                >
                    {t('navigation.app_menu_user.program')}
                </h3>
            )}

            <ProgramSwitcherPresentation
                programConfigs={programConfigs}
                isSwitching={isSwitchingProgram}
                onSwitchProgram={onSwitchProgram}
            />

            {showApplyToProgramWithoutSelectProgram && (
                <div
                    className={clsx(
                        'col-span-2',
                        'm-0',
                        'py-3',
                        'px-4',
                        'flex',
                        'items-center',
                        'relative',
                        'cursor-pointer',
                        'hover:bg-slate-grey-100',
                    )}
                    onClick={() => onNavSectionClick(NavigationSectionKey.application)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onNavSectionClick(NavigationSectionKey.application);
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faFileImport} className="mr-3 text-[21px] leading-[21px] text-gray-700" />
                    <p className="m-0 leading-4 text-gray-700">{t('settings.settings.apply_to_a_program')}</p>
                </div>
            )}

            {showApplyToProgramWithSelectProgram && (
                <div
                    className={clsx(
                        'col-span-2',
                        'm-0',
                        'py-3',
                        'px-4',
                        'flex',
                        'items-center',
                        'relative',
                        'cursor-pointer',
                        'hover:bg-slate-grey-100',
                    )}
                    onClick={() => onNavSectionClick(NavigationSectionKey.application, true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onNavSectionClick(NavigationSectionKey.application, true);
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faFileImport} className="mr-3 text-[21px] leading-[21px] text-gray-700" />
                    <p className="m-0 leading-4 text-gray-700">{t('settings.settings.apply_to_a_program')}</p>
                </div>
            )}

            <h3
                className={clsx(
                    'col-span-2',
                    'm-0',
                    'px-4',
                    'pt-6',
                    'pb-2.5',
                    'text-gray-700',
                    'text-base',
                    'cursor-default',
                    'border-0',
                    'border-b',
                    'border-solid',
                    'border-slate-grey-200',
                    'whitespace-normal',
                )}
            >
                {userName}
            </h3>

            {availableSections.map(section => (
                <a
                    key={section.name}
                    className={clsx(
                        popoverLinkClasses,
                        // Arrow icon
                        "bg-[url('~vectors/arrow_right_black_thin.svg')]",
                        'bg-no-repeat',
                        'bg-[length:6px_12px]',
                        'bg-[position:right_20px_center]',
                        "rtl:bg-[url('~vectors/arrow_left_black_thin.svg')]",
                        'rtl:bg-[position:left_20px_center]',
                    )}
                    onClick={() => onNavSectionClick(section.name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onNavSectionClick(section.name);
                        }
                    }}
                >
                    {t(`settings.settings.${section.name}`)}
                </a>
            ))}

            <a
                className={clsx(
                    'col-span-1',
                    'py-3',
                    'text-center',
                    'text-[13px]',
                    'text-gray-700',
                    'border-0',
                    'border-t',
                    'border-r',
                    'border-solid',
                    'border-slate-grey-200',
                    'rounded-bl-md',
                    'cursor-pointer',
                    'hover:bg-slate-grey-100',
                    'transition-colors',
                    'rtl:border-r-0',
                    'rtl:border-l',
                )}
                onClick={() => onNavSectionClick(NavigationSectionKey.support)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavSectionClick(NavigationSectionKey.support);
                    }
                }}
            >
                {t('navigation.app_menu_user.support')}
            </a>

            <a
                className={clsx(
                    'col-span-1',
                    'py-3',
                    'text-center',
                    'text-[13px]',
                    'text-gray-700',
                    'border-0',
                    'border-t',
                    'border-solid',
                    'border-slate-grey-200',
                    'rounded-br-md',
                    'cursor-pointer',
                    'hover:bg-slate-grey-100',
                    'transition-colors',
                )}
                onClick={() => onNavSectionClick(NavigationSectionKey.sign_out)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavSectionClick(NavigationSectionKey.sign_out);
                    }
                }}
            >
                {t('navigation.app_menu_user.sign_out')}
            </a>
        </nav>
    );
}

// Logged-out popover content component
function LoggedOutPopoverContent({
    brandNameShort,
    onLoadUrl,
    onSignIn,
}: {
    brandNameShort: string;
    onLoadUrl: (url: string) => void;
    onSignIn: () => void;
}) {
    const { t } = useTranslation();

    return (
        <nav className={clsx('font-medium', 'flex', 'flex-col')}>
            {brandNameShort && (
                <p
                    className={clsx(
                        'px-4',
                        'py-3',
                        'text-gray-700',
                        'cursor-pointer',
                        'hover:bg-slate-grey-100',
                        'm-0',
                    )}
                    onClick={() => onLoadUrl('/student-experience')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onLoadUrl('/student-experience');
                        }
                    }}
                >
                    {t('navigation.app_menu_user.why_us', { brandName: brandNameShort })}
                </p>
            )}
            <p
                className={clsx('px-4', 'py-3', 'text-gray-700', 'cursor-pointer', 'hover:bg-slate-grey-100', 'm-0')}
                onClick={() => onLoadUrl('/mba')}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onLoadUrl('/mba');
                    }
                }}
            >
                {t('navigation.app_menu_user.mba_curriculum')}
            </p>
            <p
                className={clsx('px-4', 'py-3', 'text-gray-700', 'cursor-pointer', 'hover:bg-slate-grey-100', 'm-0')}
                onClick={() => onLoadUrl('/programs/method')}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onLoadUrl('/programs/method');
                    }
                }}
            >
                {t('navigation.app_menu_user.method')}
            </p>
            <hr className="my-0 border-slate-grey-200" />
            <p
                className={clsx('px-4', 'py-3', 'text-gray-700', 'cursor-pointer', 'hover:bg-slate-grey-100', 'm-0')}
                onClick={onSignIn}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSignIn();
                    }
                }}
            >
                {t('navigation.app_menu_user.login')}
            </p>
        </nav>
    );
}

// Presentation component - renders user menu based on explicit props
export function AppMenuUserPresentation({
    avatarUrl,
    brandStyleClass,
    brandNameShort,
    popoverIsOpen,
    inOfflineMode,
    userName,
    showProgramHeader,
    programConfigs,
    isSwitchingProgram,
    showApplyToProgramWithoutSelectProgram,
    showApplyToProgramWithSelectProgram,
    availableSections,
    onTogglePopover,
    onNavSectionClick,
    onSwitchProgram,
    onLoadUrl,
    onSignIn,
    containerRef,
}: AppMenuUserPresentationProps) {
    // From SCSS: $extraPadding: 5px, $iconSize: 37px
    // width/height = iconSize + 2*extraPadding = 47px
    return (
        <div
            ref={containerRef}
            className={clsx(
                'app-menu-user',
                'w-auto',
                popoverIsOpen && 'popover-open',
                inOfflineMode && 'offline-mode',
            )}
        >
            <div
                className={clsx(
                    'group', // For hover/active state on child elements
                    'relative',
                    'w-[47px]', // iconSize + 2*extraPadding
                    'h-[47px]',
                    'text-end',
                    'rtl:text-start',
                    inOfflineMode ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
                onClick={onTogglePopover}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onTogglePopover();
                    }
                }}
            >
                <AppMenuUserAvatarPresentation
                    avatarUrl={avatarUrl}
                    brandStyleClass={brandStyleClass}
                    className="ring-1 ring-slate-grey-200"
                >
                    <span
                        className={clsx(
                            'caret-container', // Keep for test compatibility
                            'flex',
                            'items-center',
                            'justify-center',
                            'absolute',
                            'w-[13px]',
                            'h-[13px]',
                            'bg-white',
                            '-right-px',
                            'top-[25px]',
                            'rounded-full',
                            'ring-1 ring-slate-grey-200',
                        )}
                    >
                        <FontAwesomeIcon
                            icon={faCaretDown}
                            className={clsx(
                                'text-[11px]',
                                'transition-colors',
                                'duration-200',
                                // Base color - black, or coral-dark/blue-dark when popover open
                                inOfflineMode
                                    ? 'text-black'
                                    : popoverIsOpen
                                      ? brandStyleClass === 'valar'
                                          ? 'text-blue-dark'
                                          : 'text-coral-dark'
                                      : 'text-black',
                                // Hover/active states - coral by default, blue for valar (only if not offline)
                                !inOfflineMode && 'group-hover:text-coral',
                                !inOfflineMode && 'group-active:text-coral-dark',
                                !inOfflineMode && brandStyleClass === 'valar' && 'group-hover:!text-blue',
                                !inOfflineMode && brandStyleClass === 'valar' && 'group-active:!text-blue-dark',
                            )}
                        />
                    </span>
                </AppMenuUserAvatarPresentation>
            </div>

            {popoverIsOpen && !inOfflineMode && (
                <div
                    className={clsx(
                        'absolute',
                        'right-0',
                        'rtl:right-auto',
                        'rtl:left-0',
                        'top-full',
                        'mt-2',
                        'z-[10001]',
                        'bg-white',
                        'rounded-md',
                        'shadow-[0_0_10px_0_rgba(0,0,0,0.2)]',
                        'w-[249px]',
                        'p-0',
                    )}
                >
                    {/* Arrow/caret pointing up toward avatar */}
                    <div className="absolute -top-2 right-3 h-0 w-0 border-b-[8px] border-l-[8px] border-r-[8px] border-t-0 border-solid border-b-white border-l-transparent border-r-transparent rtl:left-3 rtl:right-auto" />
                    {userName ? (
                        <LoggedInPopoverContent
                            userName={userName}
                            showProgramHeader={showProgramHeader}
                            programConfigs={programConfigs}
                            isSwitchingProgram={isSwitchingProgram}
                            showApplyToProgramWithoutSelectProgram={showApplyToProgramWithoutSelectProgram}
                            showApplyToProgramWithSelectProgram={showApplyToProgramWithSelectProgram}
                            availableSections={availableSections}
                            onNavSectionClick={onNavSectionClick}
                            onSwitchProgram={onSwitchProgram}
                        />
                    ) : (
                        <LoggedOutPopoverContent
                            brandNameShort={brandNameShort}
                            onLoadUrl={onLoadUrl}
                            onSignIn={onSignIn}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// Container component props
type AppMenuUserProps = {
    user?: CurrentUser | null;
};

// Container component - handles hooks and state, delegates rendering to presentation
export function AppMenuUser({ user: userProp }: AppMenuUserProps) {
    const vanillaUser = useCurrentUser();
    const currentUser = userProp !== undefined ? userProp : vanillaUser;
    const offlineModeManager = useOfflineModeManager();
    const targetBrandConfig = useTargetBrandConfig();
    const { loadRoute } = useNavigationHelper();
    const angularContext = useAngularContext();
    const [popoverIsOpen, setPopoverIsOpen] = useState(false);
    const [isSwitchingProgram, setIsSwitchingProgram] = useState(false);
    const [changePrefProgram] = userActionsApi.useChangePrefProgramMutation();
    const userMenuRef = useRef<HTMLDivElement>(null);

    const inOfflineMode = offlineModeManager?.inOfflineMode ?? false;
    const brandNameShort = targetBrandConfig?.brandNameShort ?? '';
    const brandStyleClass = targetBrandConfig?.brandStyleClass ?? 'quantic';

    const programConfigs = useProgramSwitcherConfigs(currentUser);
    const { availableSections, allSections } = useNavigationSections(currentUser);

    const canApplyToProgram = getCanCurrentlySubmitProgramApplication(currentUser);
    const canSubmitApplications = getCanSubmitProgramApplications(currentUser);
    const supportsSelectProgram = targetBrandConfig?.supportsSelectProgram ?? false;

    const showApplyToProgramWithoutSelectProgram = !supportsSelectProgram && canApplyToProgram;
    const showApplyToProgramWithSelectProgram = supportsSelectProgram && canSubmitApplications;
    const showProgramHeader = canApplyToProgram || programConfigs.length > 0;

    // Compute avatar URL
    const avatarUrl = currentUser?.avatarUrl ?? targetBrandConfig?.avatarDefault ?? '';

    const hidePopover = useCallback(() => {
        setPopoverIsOpen(false);
    }, []);

    // Close popover when clicking outside
    useClickAway(userMenuRef, () => {
        if (popoverIsOpen) setPopoverIsOpen(false);
    });

    const togglePopover = useCallback(() => {
        if (inOfflineMode) return;
        setPopoverIsOpen(prev => !prev);
    }, [inOfflineMode]);

    const handleNavSectionClick = useCallback(
        (sectionName: string, arg?: boolean) => {
            const navSection = allSections.find(config => config.name === sectionName);
            hidePopover();
            if (navSection) {
                navSection.onClick(arg);
            }
        },
        [allSections, hidePopover],
    );

    const handleSwitchProgram = useCallback(
        async (programConfig: ProgramSwitcherProgramConfig) => {
            if (programConfig.selected || isSwitchingProgram) return;

            setIsSwitchingProgram(true);
            hidePopover();

            try {
                await changePrefProgram({
                    user_program_field_state_id: programConfig.userProgramFieldStateId,
                }).unwrap();

                // Replicate refreshUserProgramState: refresh content cache, navigate to dashboard, reload
                // oxlint-disable-next-line @typescript-eslint/no-explicit-any
                const $injector = (angularContext as any)?.$injector;
                const LearnerContentCache = $injector?.get('LearnerContentCache');
                if (LearnerContentCache) await LearnerContentCache.ensureStudentDashboard();

                $injector?.get('Navigation.NavigationHelperMixin')?.loadRoute('/dashboard');
                $injector?.get('reactRouterBridge')?.reload();
                $injector?.get('DialogModal')?.hideAlerts();
            } finally {
                setIsSwitchingProgram(false);
            }
        },
        [changePrefProgram, angularContext, isSwitchingProgram, hidePopover],
    );

    const handleLoadUrl = useCallback(
        (url: string) => {
            hidePopover();
            loadRoute(url);
        },
        [hidePopover, loadRoute],
    );

    const handleSignIn = useCallback(() => {
        hidePopover();
        loadRoute('/sign-in');
    }, [hidePopover, loadRoute]);

    return (
        <AppMenuUserPresentation
            avatarUrl={avatarUrl}
            brandStyleClass={brandStyleClass}
            brandNameShort={brandNameShort}
            popoverIsOpen={popoverIsOpen}
            inOfflineMode={inOfflineMode}
            userName={currentUser?.name ?? null}
            showProgramHeader={showProgramHeader}
            programConfigs={programConfigs}
            isSwitchingProgram={isSwitchingProgram}
            showApplyToProgramWithoutSelectProgram={showApplyToProgramWithoutSelectProgram}
            showApplyToProgramWithSelectProgram={showApplyToProgramWithSelectProgram}
            availableSections={availableSections}
            onTogglePopover={togglePopover}
            onNavSectionClick={handleNavSectionClick}
            onSwitchProgram={handleSwitchProgram}
            onLoadUrl={handleLoadUrl}
            onSignIn={handleSignIn}
            containerRef={userMenuRef}
        />
    );
}
